import { useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { moveProject, previewProjectMove } from "../store/projectsSlice";
import MoveFolderConfirmModal from "../components/MoveFolderConfirmModal/MoveFolderConfirmModal";

// Ошибка бэкенда приходит строкой (TimelinerException → 400 с текстом).
// Нет прав — 404 (так бэкенд отвечает на AccessDeniedException).
const errorText = (res) => {
  const data = res.payload?.data;
  if (typeof data === "string" && data.trim() && res.payload?.status === 400) {
    return data;
  }
  if (res.payload?.status === 404) {
    return "Нет прав на перенос или папка не найдена";
  }
  return "Не удалось переместить папку";
};

const placeKey = (id) => (id == null ? "root" : String(id));

/**
 * Перенос папки с предпросмотром изменений доступа.
 *
 * requestMoveFolder(payload, target):
 *   payload — { projectId, fromParentId, title } из FOLDER_DRAG_TYPE;
 *   target — { id, title }, id === null — корень пространства.
 * Если доступы не меняются — переносит сразу, иначе показывает окно.
 * onMoved({ projectId, fromParentId, toParentId }) — обновить списки.
 */
export const useMoveFolder = ({ onMoved }) => {
  const dispatch = useDispatch();
  const [pending, setPending] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const doMove = async (payload, target, keepAccess) => {
    const res = await dispatch(
      moveProject({
        projectId: payload.projectId,
        parentId: target.id,
        keepAccess,
      })
    );
    if (res.error) {
      toast.error(errorText(res));
      return false;
    }
    toast.success(
      target.id == null
        ? `Папка «${payload.title}» перемещена в корень пространства`
        : `Папка «${payload.title}» перемещена в «${target.title}»`
    );
    if (onMoved) {
      onMoved({
        projectId: payload.projectId,
        fromParentId: payload.fromParentId,
        toParentId: target.id,
      });
    }
    return true;
  };

  const requestMoveFolder = async (payload, target) => {
    if (!payload || payload.projectId == null || !target) return;
    if (placeKey(payload.fromParentId) === placeKey(target.id)) return;
    if (String(payload.projectId) === String(target.id)) return;

    const res = await dispatch(
      previewProjectMove({ projectId: payload.projectId, parentId: target.id })
    );
    if (res.error) {
      toast.error(errorText(res));
      return;
    }
    const preview = res.payload || {};
    if (preview.sameParent) return;

    const hasChanges =
      (preview.losing || []).length ||
      (preview.gaining || []).length ||
      (preview.changing || []).length ||
      (preview.foldersWithLostShareLinks || []).length;
    if (!hasChanges) {
      await doMove(payload, target, false);
      return;
    }
    setPending({ payload, target, preview });
  };

  const moveFolderModal = pending ? (
    <MoveFolderConfirmModal
      folderTitle={pending.payload.title}
      targetTitle={pending.target.title}
      isRoot={pending.target.id == null}
      preview={pending.preview}
      isBusy={isBusy}
      onClose={() => !isBusy && setPending(null)}
      onConfirm={async (keepAccess) => {
        setIsBusy(true);
        const ok = await doMove(pending.payload, pending.target, keepAccess);
        setIsBusy(false);
        if (ok) setPending(null);
      }}
    />
  ) : null;

  return { requestMoveFolder, moveFolderModal };
};
